import { useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { BUTTON_COLORS, DATE_FORMAT_CONCISE } from "@miplatform/constants";
import {
  addToDate,
  getDateWithAddedTime,
  subtractFromDate,
} from "@miplatform/utils";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";
import { DateRangePicker as DateRangePickerMUI } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MiButton } from "../MiButton/MiButton";

const defaultShortcutItems = [
  { label: "Today", getValue: () => [moment(), moment()] },
  {
    label: "This Week",
    getValue: () => {
      const today = moment();
      return [
        today.clone().startOf("week"),
        today.clone().endOf("week").add(2, "day"),
      ];
    },
  },
  {
    label: "Last Week",
    getValue: () => {
      const today = moment();
      const prevWeek = today.clone().subtract(1, "week");
      return [prevWeek.clone().startOf("week"), prevWeek.clone().endOf("week")];
    },
  },
  {
    label: "Last 7 Days",
    getValue: () => {
      const today = moment();
      return [today.clone().subtract(7, "days"), today.clone()];
    },
  },
  {
    label: "Current Month",
    getValue: () => {
      const today = moment();
      return [today.clone().startOf("month"), today.clone().endOf("month")];
    },
  },
  {
    label: "Next Month",
    getValue: () => {
      const today = moment();
      const startOfNextMonth = today.clone().endOf("month").add(1, "day");
      return [
        startOfNextMonth.clone().startOf("month"),
        startOfNextMonth.clone().endOf("month"),
      ];
    },
  },
];

function areDatesWithinCurrentWeek(date1, date2) {
  const startOfCurrentWeek = moment().startOf("week");
  const endOfCurrentWeek = moment().endOf("week");

  const isDate1WithinCurrentWeek = moment(date1).isBetween(
    startOfCurrentWeek,
    endOfCurrentWeek,
    null,
    "[]"
  );
  const isDate2WithinCurrentWeek = moment(date2).isBetween(
    startOfCurrentWeek,
    endOfCurrentWeek,
    null,
    "[]"
  );

  return isDate1WithinCurrentWeek && isDate2WithinCurrentWeek;
}

function IconButton({ icon, onClick, disabled }) {
  const theme = useTheme();

  return (
    <MiButton
      startIcon={{
        path: icon,
        iconColor: theme.palette.primary.main,
      }}
      size="small"
      style={{ padding: theme.spacing(1, 0.5) }}
      onClick={onClick}
      disabled={disabled}
    />
  );
}

const withStopPropagation = (fn) => (e) => {
  e.stopPropagation();
  fn(e);
};

const formatDateRange = (dateRange, format = "ddd, MMMM D") => {
  const [start, end] = dateRange;
  if (!start || !end) return null;

  const formatDay = (date) => date.format(format);
  return `${formatDay(start)} - ${formatDay(end)}, ${end.format("YYYY")}`;
};

function DateRangeButtonField({
  setOpen,
  showThisWeekButton,
  ownerState,
  value,
  format,
  InputProps,
  onRightArrowClick,
  onLeftArrowClick,
  dateRangeLength = 1,
  placeholder,
  fontSize,
}) {
  const { ref: containerRef } = InputProps;
  const [startDate, endDate] = value || [];
  const { classes } = useStyles();
  const arrowsDisabled = !startDate || !endDate;

  const handleLeftArrowClick = withStopPropagation(() => {
    if (onLeftArrowClick) {
      return onLeftArrowClick();
    }
    return ownerState.onChange([
      subtractFromDate(startDate, dateRangeLength),
      subtractFromDate(endDate, dateRangeLength),
    ]);
  });

  const handleRightArrowClick = withStopPropagation(() => {
    if (onRightArrowClick) {
      return onRightArrowClick();
    }
    return ownerState.onChange([
      addToDate(startDate, dateRangeLength),
      addToDate(endDate, dateRangeLength),
    ]);
  });

  return (
    <Box className={classes.datePickerContainer}>
      <Box
        data-testid="date-range-picker"
        className={classes.datePickerInputContainer}
      >
        <IconButton
          icon={mdiChevronLeft}
          onClick={handleLeftArrowClick}
          disabled={arrowsDisabled}
        />
        <Box
          data-testid="date-range-open-button"
          onClick={() => setOpen?.((prev) => !prev)}
          className={classes.inputField}
          ref={containerRef}
        >
          <Typography fontWeight="inherit" color="inherit" fontSize={fontSize}>
            {formatDateRange(value, format) ||
              placeholder ||
              "Select Date Range"}
          </Typography>
        </Box>
        <IconButton
          icon={mdiChevronRight}
          onClick={handleRightArrowClick}
          disabled={arrowsDisabled}
        />
      </Box>
      {showThisWeekButton && (
        <MiButton
          title="This week"
          variant="contained"
          color={BUTTON_COLORS.PRIMARY}
          onClick={() =>
            ownerState.onChange([
              moment().startOf("week").add(1, "days"),
              moment().endOf("week").add(1, "days"),
            ])
          }
          disabled={areDatesWithinCurrentWeek(startDate, endDate)}
        />
      )}
    </Box>
  );
}

DateRangeButtonField.fieldType = "single-input";

function DateRangePickerComponent({
  calendars,
  format,
  onChange,
  rangePosition,
  onRangePositionChange,
  showShortCutItems,
  shortcutItems = defaultShortcutItems,
  showThisWeekButton = true,
  slots,
  slotProps,
  label,
  value,
  onRightArrowClick,
  onLeftArrowClick,
  maxDate,
  dateRangeLength,
  disablePast,
  placeholder,
  fontSize,
}) {
  const [open, setOpen] = useState(false);

  return (
    <DateRangePickerMUI
      slots={{
        field: DateRangeButtonField,
        ...slots,
      }}
      slotProps={{
        field: {
          setOpen,
          showThisWeekButton,
          onRightArrowClick,
          onLeftArrowClick,
          dateRangeLength,
          placeholder,
          fontSize,
        },
        shortcuts: {
          items: showShortCutItems ? shortcutItems : [],
        },
        ...slotProps,
      }}
      label={label}
      disablePast={disablePast}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      calendars={calendars}
      format={format}
      onChange={onChange}
      rangePosition={rangePosition}
      onRangePositionChange={onRangePositionChange}
      value={value}
      maxDate={maxDate}
    />
  );
}

/**
 * A date range picker component that wraps DateRangePickerComponent with localization support.
 *
 * @param {Object} props - The component props
 * @param {Object} [props.dateRange] - The current date range object
 * @param {Date|string|null} [props.dateRange.startDate] - The start date of the range
 * @param {Date|string|null} [props.dateRange.endDate] - The end date of the range
 * @param {Function} props.onDateChange - Callback function called when date range changes
 * @param {string} [props.label] - Label for the picker
 * @param {number} [props.maxSelectionEnd] - Maximum selectable amount of days in the future
 * @param {number} [props.exactRangeLength] - Exact number of days for the range
 * @param {number} [props.calendarCount=1] - Number of calendars to display
 * @param {boolean} [props.disablePast] - Whether to disable past dates
 * @param {boolean} [props.showShortCutItems] - Whether to show shortcut items
 * @param {Array} [props.shortcutItems] - Custom shortcut items
 * @param {boolean} [props.showThisWeekButton=true] - Whether to show "this week" button
 * @param {string} [props.dateFormat] - Date format string, defaults to 'ddd, MMMM D'
 * @param {string} [props.rangePosition] - Current range position
 * @param {Function} [props.onRangePositionChange] - Callback for range position changes
 * @param {Function} [props.onRightArrowClick] - Callback for right arrow click
 * @param {Function} [props.onLeftArrowClick] - Callback for left arrow click
 * @param {Object} [props.slots] - MUI slots override
 * @param {Object} [props.slotProps] - MUI slot props override
 * @param {string} [props.placeholder] - Placeholder text for the picker
 * @param {string} [props.fontSize] - Font size for the picker display
 * @returns {JSX.Element} The rendered date range picker component
 */
export function MiDateRangePicker({
  dateRange,
  onDateChange,
  label,
  maxSelectionEnd,
  exactRangeLength,
  disablePast,
  calendarCount = 1,
  showShortCutItems,
  shortcutItems,
  showThisWeekButton = true,
  dateFormat = DATE_FORMAT_CONCISE,
  onRightArrowClick,
  onLeftArrowClick,
  slots,
  slotProps,
  placeholder,
  fontSize,
}) {
  const [rangePosition, setRangePosition] = useState("start");

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateRangePickerComponent
        showShortCutItems={showShortCutItems}
        shortcutItems={shortcutItems}
        showThisWeekButton={showThisWeekButton}
        rangePosition={rangePosition}
        format={dateFormat}
        calendars={calendarCount}
        disablePast={disablePast}
        dateRangeLength={exactRangeLength}
        label={label}
        slots={slots}
        slotProps={slotProps}
        placeholder={placeholder}
        fontSize={fontSize}
        value={[
          dateRange?.startDate ? moment(dateRange.startDate) : null,
          dateRange?.endDate ? moment(dateRange.endDate) : null,
        ]}
        onRangePositionChange={exactRangeLength ? () => null : setRangePosition}
        maxDate={
          maxSelectionEnd && dateRange?.startDate
            ? rangePosition === "end" &&
              moment(
                getDateWithAddedTime(dateRange.startDate, {
                  d: maxSelectionEnd,
                })
              )
            : null
        }
        onChange={([startDate, endDate]) => {
          if (!startDate && !endDate) {
            onDateChange(null, null);
            return;
          }

          if (maxSelectionEnd && startDate && endDate) {
            const cappedEndDate = moment.min(
              endDate,
              moment(getDateWithAddedTime(startDate, { d: maxSelectionEnd }))
            );
            onDateChange(startDate, cappedEndDate);
            return;
          }

          onDateChange(startDate || null, endDate || null);
        }}
        onRightArrowClick={onRightArrowClick}
        onLeftArrowClick={onLeftArrowClick}
      />
    </LocalizationProvider>
  );
}

const useStyles = makeStyles()((theme) => ({
  datePickerInputContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(4),
    whiteSpace: "nowrap",
  },
  datePickerContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
    },
  },
  inputField: {
    border: "none",
    minWidth: 220,
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.bold,
    textAlign: "center",
    cursor: "pointer",
    "&:focus-visible": {
      outline: "none",
    },
  },
}));
